import React, { useState } from 'react';

import {
  Box,
  ClickAwayListener,
  IconButton,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { makeStyles } from 'tss-react/mui';

interface IAppHelpTooltipButtonProps {
  text?: string;
  content?: React.ReactNode;
  placement?: TooltipProps['placement'];
  scrollableContent?: boolean;
}

const useStyles = makeStyles<Theme>()((theme) => ({
  tooltip: {
    maxWidth: '280px',
    whiteSpace: 'normal',
    lineHeight: 1.5,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1.25),
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5),
  },
  tooltipText: {
    whiteSpace: 'pre-wrap',
  },
  scrollableContent: {
    maxHeight: '60vh',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },
  button: {
    padding: 0,
    color: theme.palette.text.secondary,
  },
  icon: {
    fontSize: 18,
  },
}));

const AppHelpTooltipButton: React.FC<IAppHelpTooltipButtonProps> = ({
  text,
  content,
  placement = 'top',
  scrollableContent = false,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { classes } = useStyles(theme);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <Tooltip
          PopperProps={{ disablePortal: true }}
          placement={placement}
          onClose={() => setOpen(false)}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          classes={{ tooltip: classes.tooltip }}
          title={
            <Box
              className={
                scrollableContent ? classes.scrollableContent : undefined
              }>
              {content ?? (
                <Typography variant="text-sm" className={classes.tooltipText}>
                  {text ?? ''}
                </Typography>
              )}
            </Box>
          }>
          <IconButton
            size="small"
            aria-label="help"
            className={classes.button}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}>
            <HelpOutlineIcon className={classes.icon} />
          </IconButton>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

export default AppHelpTooltipButton;
